/**
 * ClubSettingsDangerPage - Dangerous Actions
 * Issue #901: Split from monolithic ClubSettingsPage
 *
 * Sections:
 * - Transfer Ownership
 * - Delete Club
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useClub, useDeleteClub, useTransferOwnership } from '../../hooks/useClubs';
import { useClubMembers } from '../../hooks/useMembers';
import { TopBar, Button, Input, Card, Skeleton } from '../../components';
import { AlertTriangle, Trash2, UserCog } from 'lucide-react';

export default function ClubSettingsDangerPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Data fetching
  const { data: clubData, isLoading: clubLoading, error: clubError } = useClub(slug || '');
  const club = clubData?.club;
  const membership = clubData?.membership;
  const isOwner = membership?.is_owner || false;

  const { data: membersData, isLoading: membersLoading } = useClubMembers(slug || '');
  const members = membersData?.members || [];

  // Mutations
  const deleteClubMutation = useDeleteClub();
  const transferOwnershipMutation = useTransferOwnership(slug || '');

  // Dialog state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [selectedNewOwner, setSelectedNewOwner] = useState('');
  const [postTransferRole, setPostTransferRole] = useState<'leader' | 'member' | 'leave'>('leader'); // #714

  // Success message
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Clear success message after delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Handlers
  const handleTransferOwnership = async () => {
    if (!selectedNewOwner) return;

    try {
      await transferOwnershipMutation.mutateAsync({
        newOwnerId: selectedNewOwner,
        oldOwnerRole: postTransferRole, // #714
      });
      setSuccessMessage('Ownership transferred successfully');
      setShowTransferDialog(false);
      setSelectedNewOwner('');
      setPostTransferRole('leader');

      // Navigate away since user is no longer owner (or left the club)
      setTimeout(() => navigate(postTransferRole === 'leave' ? '/home' : `/clubs/${slug}`), 2000);
    } catch {
      // Error toast handled by useOroMutation
    }
  };

  const handleDeleteClub = async () => {
    if (!slug || !club) return;
    if (deleteConfirmText !== club.name) return;

    try {
      await deleteClubMutation.mutateAsync(slug);
      setSuccessMessage('Club deleted successfully');
      setShowDeleteConfirm(false);

      // Navigate to home
      setTimeout(() => navigate('/home'), 2000);
    } catch {
      // Error toast handled by useOroMutation
    }
  };

  const isLoading = clubLoading || membersLoading;

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Danger Zone" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="max-w-2xl mx-auto p-4 space-y-4">
            <Skeleton className="h-32 w-full rounded-xl" />
            <Skeleton className="h-32 w-full rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  // Error/permission state
  if (clubError || !isOwner || !club) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Danger Zone" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-12 text-center">
            <p className="text-text-muted">
              {clubError ? 'Unable to load settings' : 'Only club owners can access settings'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Leaders who can receive ownership
  const adminMembers = members.filter(m => m.role === 'leader' && m.user_id !== club.owner_id);
  const canTransferOwnership = adminMembers.length > 0;

  const anyError = deleteClubMutation.error || transferOwnershipMutation.error;

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar title="Danger Zone" onBack={() => navigate(-1)} />

      <div className="flex-1 overflow-y-auto pt-14">
        <div className="max-w-2xl mx-auto p-4 space-y-6">
          {/* Success Message */}
          {successMessage && (
            <div className="rounded-lg border border-oro-success/40 bg-oro-success/10 p-4">
              <p className="text-sm text-oro-success">{successMessage}</p>
            </div>
          )}

          {/* Error Message */}
          {anyError && (
            <div className="rounded-lg border border-oro-error/40 bg-oro-error/10 p-4">
              <p className="text-sm text-oro-error">
                {(anyError as Error).message || 'An error occurred'}
              </p>
            </div>
          )}

          {/* Warning Header */}
          <div className="flex items-center gap-3 p-4 bg-oro-warning/10 border border-oro-warning/30 rounded-xl">
            <AlertTriangle className="w-6 h-6 text-oro-warning shrink-0" />
            <div>
              <h2 className="font-semibold text-oro-warning">Danger Zone</h2>
              <p className="text-sm text-text-secondary">
                These actions are irreversible. Please proceed with caution.
              </p>
            </div>
          </div>

          {/* Transfer Ownership Section */}
          <section className="space-y-4">
            <div className="bg-oro-surface border border-oro-warning/30 rounded-2xl p-4">
              <h3 className="font-medium text-oro-warning mb-2 flex items-center gap-2">
                <UserCog className="w-4 h-4" />
                Transfer Ownership
              </h3>
              <p className="text-sm text-text-secondary mb-4">
                Transfer club ownership to another leader. You can choose your role after the transfer.
              </p>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowTransferDialog(true)}
                disabled={!canTransferOwnership}
                className="border-oro-warning/40 text-oro-warning"
              >
                Transfer Ownership
              </Button>
              {!canTransferOwnership && (
                <p className="text-xs text-text-muted mt-2">
                  Promote a member to leader first before you can transfer ownership.
                </p>
              )}
            </div>
          </section>

          {/* Delete Club Section */}
          <section className="space-y-4">
            <div className="bg-oro-surface border border-oro-error/30 rounded-2xl p-4">
              <h3 className="font-medium text-oro-error mb-2 flex items-center gap-2">
                <Trash2 className="w-4 h-4" />
                Delete Club
              </h3>
              <p className="text-sm text-text-secondary mb-4">
                Permanently delete this club and all associated data including events, messages, and member records. This action cannot be undone.
              </p>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowDeleteConfirm(true)}
                className="border-oro-error/40 text-oro-error"
              >
                Delete Club
              </Button>
            </div>
          </section>
        </div>
      </div>

      {/* Transfer Ownership Dialog */}
      {showTransferDialog && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Transfer Ownership</h3>

            {/* New Owner Selection */}
            <p className="text-text-secondary mb-3">
              Select a leader to become the new club owner:
            </p>
            <div className="space-y-2 mb-6">
              {adminMembers.map((admin) => (
                <label key={admin.user_id} className="flex items-center gap-3 p-3 rounded-lg border border-oro-border hover:bg-oro-surface-alt/30 cursor-pointer">
                  <input
                    type="radio"
                    name="newOwner"
                    value={admin.user_id}
                    checked={selectedNewOwner === admin.user_id}
                    onChange={(e) => setSelectedNewOwner(e.target.value)}
                    className="text-accent"
                  />
                  <div>
                    <p className="font-medium">{admin.public_display_name}</p>
                    <p className="text-sm text-text-muted">{admin.email}</p>
                  </div>
                </label>
              ))}
            </div>

            {/* Post-Transfer Role Selection (#714) */}
            <p className="text-text-secondary mb-3">
              Your role after transfer:
            </p>
            <div className="space-y-2 mb-6">
              <label className="flex items-center gap-3 p-3 rounded-lg border border-oro-border hover:bg-oro-surface-alt/30 cursor-pointer">
                <input
                  type="radio"
                  name="postTransferRole"
                  value="leader"
                  checked={postTransferRole === 'leader'}
                  onChange={() => setPostTransferRole('leader')}
                  className="text-accent"
                />
                <div>
                  <p className="font-medium">Stay as Leader</p>
                  <p className="text-sm text-text-muted">Retain admin privileges</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 rounded-lg border border-oro-border hover:bg-oro-surface-alt/30 cursor-pointer">
                <input
                  type="radio"
                  name="postTransferRole"
                  value="member"
                  checked={postTransferRole === 'member'}
                  onChange={() => setPostTransferRole('member')}
                  className="text-accent"
                />
                <div>
                  <p className="font-medium">Become Regular Member</p>
                  <p className="text-sm text-text-muted">Lose admin access</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 rounded-lg border border-oro-border hover:bg-oro-surface-alt/30 cursor-pointer">
                <input
                  type="radio"
                  name="postTransferRole"
                  value="leave"
                  checked={postTransferRole === 'leave'}
                  onChange={() => setPostTransferRole('leave')}
                  className="text-accent"
                />
                <div>
                  <p className="font-medium text-oro-warning">Leave Club</p>
                  <p className="text-sm text-text-muted">Remove yourself from the club</p>
                </div>
              </label>
            </div>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowTransferDialog(false);
                  setSelectedNewOwner('');
                  setPostTransferRole('leader');
                }}
                disabled={transferOwnershipMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleTransferOwnership}
                loading={transferOwnershipMutation.isPending}
                disabled={!selectedNewOwner}
                className="bg-oro-warning hover:bg-oro-warning/90"
              >
                Transfer Ownership
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Delete Club Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4 text-oro-error">Delete Club</h3>
            <p className="text-text-secondary mb-4">
              Are you absolutely sure? This action cannot be undone.
            </p>
            <p className="text-sm text-text-muted mb-4">
              Type <strong className="text-text-primary">{club.name}</strong> to confirm:
            </p>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={club.name}
              className="mb-6"
            />
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmText('');
                }}
                disabled={deleteClubMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleDeleteClub}
                loading={deleteClubMutation.isPending}
                disabled={deleteConfirmText !== club.name}
                className="bg-oro-error hover:bg-oro-error/90"
              >
                Delete Forever
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
